import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { motion } from "motion/react";

import { cn } from "../../../lib/utils";
import { DownloadButton } from "@/global/base/download-button";
import {
  capitalizeFirstLetter,
  getLabelForScheduleType,
  getTotalCapsulesLabel,
  handleCatch,
  pluralizeUnit,
  prescriberName,
} from "../../../shared/helpers";
import moment from "moment";
import CalendarComponent from "../Calendar/Calendar";
import useRoles from "../../../hooks/useRoles";
import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";
import APIVariables from "../../../services/api-variables";
import Axios from "../../../services/Axios";
import { ToolTipHover } from "./components/ToolTipHover";
import Loader from "../Loader/Loader";
import {
  dosageEditLinkClass,
  dosageSupplyCardClass,
  dosageSupplyFieldLabel,
  fieldLabel,
  fieldValue,
  pafSectionLabelClass,
  VIEW_PAF_ROOT_CLASS,
} from "./constants/viewPafConstants";
import {
  downloadPafPdfForPatient,
  historyCapsules,
  historyDispensingDate,
  historyDosageStrength,
  historyIndicationLabel,
  historyMoleculeLabel,
  historyNCycles,
  historyPafDate,
  historyTotalSupplyWeeks,
  historyWeeksPerCycle,
} from "./utils/viewPafHistoryHelpers";
import { getViewPafRouteContext } from "./utils/viewPafRouteUtils";
import {
  extractIndication,
  formatLastNegativePregnancyDate,
  getPafStatus,
  hasDispensingBrandAssigned,
  isInpatientPharmacyEditablePafStatus,
  markInRedLastNegativePregnancyTest,
  markInRedPrescribedOnField,
  normalizePafPrescription,
  resolvePatientCategoryLabel,
} from "./utils/viewPafFormLogic";
import { useViewPafPrescriptionFields } from "./hooks/useViewPafPrescriptionFields";
import { useViewPafDosageColumns } from "./hooks/useViewPafDosageColumns";
import { usePrescriptionCompliance } from "./hooks/usePrescriptionCompliance";
import ComplianceIndicator from "./components/ComplianceIndicator";
import { resolveComplianceIndicator } from "./utils/prescriptionComplianceHelpers";
import NonComplianceHistoryPanel from "./components/NonComplianceHistoryPanel";
import PrescriptionAuditTrailCard from "./components/PrescriptionAuditTrailCard";
import PrescriptionReviewSummary from "./components/PrescriptionReviewSummary";
import PafEditBrandDateFields from "./components/PafEditBrandDateFields";
import BlobLoader from "../Loader/BlobLoader";
import { fetchPrescriptionAuditTrail } from "../../../services/auditTrail";

const ViewPAF = ({
  receivedForm,
  handleChangeSubmitedForm,
  isFormApproved,
  isCreateNewPAF,
  handleAditionalOptionsChange,
  aditionalOptionsState,
  dispensingForm,
  deletePropertyValue,
  setCapsule_changes_tracker,
  capsule_changes_tracker,
  prescriptionField,
  updateChildObject,
  hideEditButton,
  isPafEditMode = false,
  onEditFormChange,
  checkedItems,
  patientDetails,
  historyPafs = [],
  footer,
  onCreateNewCycle,
}) => {
  const [form, setForm] = useState({});
  const [treatmentPanel, setTreatmentPanel] = useState("current");
  const reducedMotion = usePrefersReducedMotion();

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const {
    isRenewal,
    typeOfForRenew,
    isPatientInitiationOrRenew,
    pathSegment3,
  } = getViewPafRouteContext(pathname);

  const textMotionProps = (delay = 0) => ({
    initial: reducedMotion ? false : { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: {
      delay: reducedMotion ? 0 : 0.5 + delay,
      duration: reducedMotion ? 0 : 0.35,
      ease: [0.22, 1, 0.36, 1],
    },
  });

  useEffect(() => {
    !!updateChildObject && updateChildObject(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const [isLastPregancyTestActive, setIsLastPregancyTestActive] =
    useState(false);
  const [isPrescribedOn, setIsPrescribedOn] = useState(false);
  const { isPharmacy, isPrescriber, isConnectedNurse, storageData, canViewAuditTrail } =
    useRoles();
  const [initialConfirmation, setInitialConfirmation] = useState([]);

  const is_form_ready_form_approve =
    isPharmacy && isInpatientPharmacyEditablePafStatus(form);

  const displayWeeks = form?.schedule_display_type === "weeks";

  const {
    complaintUserKey,
    complaints,
    complaintsLoading,
    indicatorsByPrescriptionId,
    getIndicator,
  } = usePrescriptionCompliance({
    patientDetails,
    form,
    receivedForm,
    loadComplaints: treatmentPanel === "noncompliance",
  });

  const currentComplianceIndicator = useMemo(
    () => getIndicator(form?.id ?? receivedForm?.id),
    [form?.id, receivedForm?.id, getIndicator]
  );

  const auditPrescriptionId = form?.id ?? receivedForm?.id;
  const showAuditTrailTab =
    canViewAuditTrail && !isCreateNewPAF && Boolean(auditPrescriptionId);
  const [auditTrailEntry, setAuditTrailEntry] = useState(null);
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);
  const [auditTrailError, setAuditTrailError] = useState("");

  useEffect(() => {
    if (treatmentPanel !== "audit" || !auditPrescriptionId) return undefined;

    const controller = new AbortController();
    setAuditTrailLoading(true);
    setAuditTrailError("");
    fetchPrescriptionAuditTrail(auditPrescriptionId, {
      signal: controller.signal,
    })
      .then((entry) => setAuditTrailEntry(entry))
      .catch((error) => {
        if (error?.code === "ERR_CANCELED") return;
        handleCatch(error);
        setAuditTrailEntry(null);
        setAuditTrailError("Could not load the audit trail for this prescription.");
      })
      .finally(() => setAuditTrailLoading(false));

    return () => controller.abort();
  }, [treatmentPanel, auditPrescriptionId]);

  const scheduleDisplayType =
    prescriptionField?.schedule_display_type || form?.schedule_display_type;

  // check if new-capsules exists , if not, initialize these values
  const newCapsuleExist =
    Array.isArray(form?.strengths) &&
    form?.strengths.map((strength) => strength?.new_capsules);

  useEffect(() => {
    if (Array.isArray(form?.strengths) && !isFormApproved) {
      const updatedForm = {
        ...form,
        strengths: form.strengths.map((strength) => ({
          ...strength,
          new_capsules: null,
          new_strength_id: null,
          new_capsules_other: null,
          is_new_other_capsules: false,
        })),
      };
      setForm(updatedForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!newCapsuleExist]);

  useEffect(() => {
    if (!isCreateNewPAF && isPharmacy && is_form_ready_form_approve) {
      const strengths = form?.strengths;

      const history = {
        old_preganacy_test: receivedForm.pregnancy_test,
        old_n_cycles: receivedForm.n_cycles,
        strengths,
      };
      setCapsule_changes_tracker(history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_form_ready_form_approve]);

  useLayoutEffect(() => {
    if (receivedForm == null) {
      if (!isCreateNewPAF) {
        setForm({});
      }
      return;
    }
    setForm(normalizePafPrescription({ ...receivedForm }));
    setIsLastPregancyTestActive(false);
    setIsPrescribedOn(false);
  }, [receivedForm, isCreateNewPAF]);

  const editField = (_, fieldName) => {
    if (fieldName === "lastNegativePregnanacyTest") {
      setIsLastPregancyTestActive(true);
    }

    if (fieldName === "prescribedOn") {
      setIsPrescribedOn(true);
    }
  };

  const disableAditionalOptions = () => {
    const status = getPafStatus(form);
    return (
      status === "Approved" ||
      status === "Rejected" ||
      status === "Dispensed" ||
      !!isFormApproved ||
      !isPharmacy
    );
  };

  const showDosageFieldEdits =
    isPafEditMode || (!!hideEditButton && isFormApproved === null);

  const [prescriptionForFields, setPrescriptionForFields] = useState([]);
  // Get confirmations product from BE
  const getPrescription = async () => {
    if (isPharmacy || isPafEditMode) {
      const data = await Axios.get(
        APIVariables.PRESCRIPTION_FIELDS +
        (!!form?.product_id && form?.product_id) +
        `/${form?.patient?.type}`
      ).catch((error) => {
        handleCatch(error);
      });
      setPrescriptionForFields(data.data);
    }
  };

  useEffect(() => {
    !isCreateNewPAF && !!form?.product_id && getPrescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.product_id, isPafEditMode]);

  useEffect(() => {
    if (isPafEditMode && typeof onEditFormChange === "function") {
      onEditFormChange(form);
    }
  }, [form, isPafEditMode, onEditFormChange]);

  // if you select one option , is removed from dropdown

  const strengthValueToExclude =
    !isCreateNewPAF && !isPafEditMode
      ? (form?.strengths || []).map((strength) =>
        !!strength.strength_value ? strength.strength_value : undefined
      )
      : [];
  const modifiedCapsuleStrengths = (prescriptionForFields?.strengths || [])
    ?.filter(
      (strength) =>
        !strengthValueToExclude?.includes(parseFloat(strength.value))
    )
    ?.map((strength) => {
      return {
        id: strength.id,
        label: strength.value,
      };
    });

  const CapsulesperCycle = prescriptionForFields?.capsule_cycle
    ?.filter((strength) => strength.id !== -1 && strength.value !== null)
    .map((strength) => {
      return {
        id: strength.id,
        label: strength.value,
      };
    });

  const modifiedNCycles = prescriptionForFields?.n_cycles?.map((strength) => {
    return {
      id: strength.id,
      label: strength.value,
    };
  });

  const modifiedWeeksCycles = prescriptionForFields?.n_weeks?.map(
    (strength) => {
      return {
        id: strength.id,
        label: strength.value,
      };
    }
  );

  const modifiedWeeksPerCycle = prescriptionForFields?.weeks_per_cycle?.map(
    (strength) => {
      return {
        id: strength?.id,
        label: strength?.value,
      };
    }
  );

  const oldValueType =
    displayWeeks && form?.history
      ? form?.history?.n_weeks === null || form?.history?.n_weeks === 0
        ? form?.history?.n_weeks_other
        : form?.history?.n_weeks
      : form?.history?.n_cycles;

  const oldValueWeeksPerCycle =
    scheduleDisplayType === "weeks_per_cycle" && form?.history?.weeks_per_cycle;

  const newStrengths = Array.isArray(form?.strengths)
    ? form?.strengths?.map((strength) => ({
      id: strength.strength_id,
      label: strength.strength_value + "mg",
    }))
    : null;

  const newCapsules = Array.isArray(form?.strengths)
    ? form?.strengths.map((capsule) => ({
      value: capsule?.new_capsules
        ? capsule?.new_capsules
        : capsule?.capsules,
      label: `${capsule.new_capsules ? capsule.new_capsules : capsule.capsules
        } ${pluralizeUnit(capitalizeFirstLetter(form?.unit_label || 'capsule'))}`,
    }))
    : null;

  const placeholder = "Select"; // Replace with your desired placeholder value
  const populatedRename = Array.from(
    { length: form?.strengths?.length },
    () => placeholder
  );

  !!CapsulesperCycle &&
    CapsulesperCycle?.forEach((item) => {
      const index = prescriptionField?.strengths?.findIndex(
        (element) => element.id === item.id
      );
      if (index !== -1) {
        populatedRename[index] = item + 1;
      }
    });

  const other_capsules_value =
    !isCreateNewPAF && form?.strengths?.map((other_value) => other_value);

  const [editingState, setEditingState] = useState({
    strengths: false,
    capsules: false,
    n_cycles: false,
    number_weeks: false,
  });

  const [editing2State, setEditing2State] = useState({
    capsules: Array(form?.strengths?.length).fill(false),
    strengths: Array(form?.strengths?.length).fill(false),
  });

  const handleEdit = (index, type) => {
    const newEditing2States = { ...editing2State };
    newEditing2States[type][index] = true;
    setEditing2State(newEditing2States);
  };

  const { handlePrescriptionFields } = useViewPafPrescriptionFields({
    form,
    setForm,
    displayWeeks,
    handleAditionalOptionsChange,
  });

  const validationContext = useMemo(
    () => ({
      isRenewal,
      typeOfForRenew,
      prescriptionField,
    }),
    [isRenewal, typeOfForRenew, prescriptionField]
  );

  const {
    renderCapsuleStrengthColumn,
    renderCapsulePerCycleColumn,
    renderNumberOfCyclesColumn,
    renderTotalSupplyColumn,
  } = useViewPafDosageColumns({
    form,
    isRenewal,
    scheduleDisplayType,
    displayWeeks,
    prescriptionField,
    isFormApproved,
    hideEditButton,
    showDosageFieldEdits,
    editingState,
    setEditingState,
    editing2State,
    setEditing2State,
    handlePrescriptionFields,
    handleEdit,
    textMotionProps,
    modifiedCapsuleStrengths,
    newStrengths,
    newCapsules,
    CapsulesperCycle,
    modifiedNCycles,
    modifiedWeeksCycles,
    modifiedWeeksPerCycle,
    oldValueType,
    oldValueWeeksPerCycle,
    validationContext,
    isPafEditMode,
  });

  const displayConditionallyApprovedBy = () => {
    const pafStatus = getPafStatus(form);
    if (
      pafStatus === "Approved" ||
      pafStatus === "Rejected" ||
      pafStatus === "Dispensed" ||
      pafStatus === "Overdue" ||
      pafStatus === "Status Update"
    ) {
      // Status Update: show pharmacist name from API when already actioned (not the default Awaiting)
      return form?.approved_or_rejected_by;
    }

    if (
      pafStatus === "Submitted" &&
      (isFormApproved === null || isFormApproved === undefined)
    ) {
      return "Awaiting approval";
    }

    if (
      pafStatus === "Submitted" &&
      (isFormApproved !== null || isFormApproved !== undefined)
    ) {
      return storageData?.first_name + " " + storageData?.last_name;
    }
    return "Awaiting approval";
  };

  const displayConditionallyPrescriberName = () => {
    if (isCreateNewPAF) return prescriberName();
    if (
      form?.prescriber &&
      form?.prescriber?.first_name &&
      form?.prescriber?.last_name
    )
      return form?.prescriber?.first_name + " " + form?.prescriber?.last_name;
    return "";
  };

  const getPharmacyFromLocalStorage = () => {
    return storageData?.hospital?.value + ", " + storageData?.hospital?.address;
  };

  const deleteProperty = (propName) => {
    deletePropertyValue(propName);
  };

  // Used at Create PAF
  const handedHospitalName =
    storageData?.hospital?.value + ", " + storageData?.hospital?.address;

  const is_preganacy_test_changes = !!capsule_changes_tracker?.pregnancy_test;

  const handleChange = (event, index) => {
    if (isInpatientPharmacyEditablePafStatus(form) && isPharmacy) {
      handleChangeSubmitedForm("confirmation", event, index);
    }
  };

  // Set up the initial form array
  useEffect(() => {
    if (form && Array.isArray(form?.prescription_confirmation_risks)) {
      const copiedArray = [...form?.prescription_confirmation_risks];
      setInitialConfirmation(copiedArray);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!form?.prescription_confirmation_risks]);

  /** Merge API `form.patient`, flat wizard fields (`patient_dob`, etc.), and optional `patientDetails` (e.g. getUserInfo on create). */
  const summaryPatient = useMemo(() => {
    const nested =
      form?.patient && typeof form.patient === "object" ? form.patient : {};
    const hasFlatWizardFields =
      form &&
      (form.patient_dob != null ||
        form.patient_initials != null ||
        form.id != null);
    const fromFlat = hasFlatWizardFields
      ? {
        id: form.id ?? nested.id,
        patient_initials: form.patient_initials ?? nested.patient_initials,
        dob: form.patient_dob ?? form.dob ?? nested.dob,
        cycles_completed:
          form.cycles_completed ??
          form.number_of_cycles_completed ??
          nested.cycles_completed,
        number_of_cycles_completed:
          form.number_of_cycles_completed ??
          nested.number_of_cycles_completed,
        total_mg_received:
          form.total_mg_received ??
          form.total_mg ??
          nested.total_mg_received ??
          nested.total_mg,
      }
      : {};
    return {
      ...nested,
      ...fromFlat,
      ...(patientDetails || {}),
    };
  }, [patientDetails, form]);

  const formatSummaryDob = () => {
    const d = summaryPatient?.dob;
    if (!d) return "—";
    return moment(d).format("DD / MM / YYYY");
  };

  const dispensingDateDisplay = () => {
    if (getPafStatus(form) !== "Dispensed") return "—";
    const d = form?.amended_dispensed_date || form?.dispensed_date;
    return d ? moment(d).format("DD-MMM-YYYY") : "—";
  };

  const cyclesCompletedSummary =
    summaryPatient?.cycles_completed ??
    summaryPatient?.number_of_cycles_completed ??
    form?.patient?.cycles_completed ??
    null;

  const totalMgSummary =
    summaryPatient?.total_mg_received ??
    summaryPatient?.total_mg ??
    form?.patient?.total_mg_received ??
    null;

  const treatingHospitalTopBar =
    form?.hospital_name_and_address ||
    form?.patient?.hospital ||
    patientDetails?.hospital ||
    patientDetails?.treatingHospital ||
    summaryPatient?.hospital ||
    summaryPatient?.treatingHospital ||
    (isCreateNewPAF ? handedHospitalName : null);

  // const renderComplianceIndicator = () => {
  //   const st = form?.status;
  //   const base =
  //     "inline-flex items-center gap-2 text-sm font-semibold";
  //   if (st === "Rejected") {
  //     return (
  //       <div className={cn(base, "text-[#e41818]")}>
  //         <span aria-hidden>✕</span> Not compliant
  //       </div>
  //     );
  //   }
  //   if (st === "Dispensed" || st === "Approved") {
  //     return (
  //       <div className={cn(base, "text-[#00a03b]")}>
  //         <span aria-hidden>✓</span> Compliant
  //       </div>
  //     );
  //   }
  //   return (
  //     <div className={cn(base, "text-[#ff9900]")}>Pending review</div>
  //   );
  // };

  const showPafLoader =
    !isCreateNewPAF &&
    (receivedForm == null ||
      (receivedForm?.id != null &&
        (form?.id == null ||
          String(form.id) !== String(receivedForm.id))));

  if (showPafLoader) {
    return (
      <div className={VIEW_PAF_ROOT_CLASS} data-testid="view-paf-root">
        <div
          className="flex w-full items-center justify-center [&_img]:mx-auto [&_img]:mt-0 [&_img]:block [&_img]:h-28"
          data-testid="view-paf-loader"
        >
          <Loader />
        </div>
      </div>
    );
  }

  return form ? (
    <div className={VIEW_PAF_ROOT_CLASS} data-testid="view-paf-root">
      <div className="flex w-full flex-col overflow-hidden rounded-lg">
        <div className="px-5 pb-8 pt-5" data-testid="view-paf-patient-summary">
          <div
            className={cn(
              "grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2 lg:grid-cols-3",
              isCreateNewPAF ? "xl:grid-cols-5" : "xl:grid-cols-6"
            )}
          >
            <div>
              <div className={fieldLabel} data-testid="view-paf-patient-id-label">Patient ID</div>
              <div className={fieldValue}>
                {summaryPatient?.id ?? "—"}
              </div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-patient-initials-label">Patient Initials</div>
              <div className={fieldValue}>
                {summaryPatient?.patient_initials ?? "—"}
              </div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-category-label">Category</div>
              <div className={fieldValue}>{resolvePatientCategoryLabel(form, pathSegment3) ?? "—"}</div>
            </div>
            <div>
              <div className={fieldLabel} data-testid="view-paf-date-of-birth-label">Date of Birth</div>
              <div className={fieldValue}>{formatSummaryDob()}</div>
            </div>
            {isCreateNewPAF ? (
              <div>
                <div className={fieldLabel} data-testid="view-paf-treating-hospital-label">Treating Hospital</div>
                <div className={fieldValue}>
                  {treatingHospitalTopBar ?? "—"}
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className={fieldLabel} data-testid="view-paf-cycles-completed-label">Number of Cycles completed</div>
                  <div className={fieldValue}>
                    {cyclesCompletedSummary != null
                      ? `${cyclesCompletedSummary} Cycles`
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className={fieldLabel} data-testid="view-paf-total-mg-received-label">Total Mg received since started</div>
                  <div className={fieldValue}>
                    {totalMgSummary != null ? `${totalMgSummary}mg` : "—"}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex min-h-0 w-full flex-1 items-stretch">
          <aside
            className="w-[210px] shrink-0 px-3.5 py-5"
            data-testid="view-paf-treatment-overview"
          >
            <div className="mb-[18px] text-[13px] font-bold text-[#3c3c3c]">
              Treatment Overview
            </div>

            {/* Tab: current — main PAF detail (`treatmentPanel === "current"`) */}
            <button
              type="button"
              data-testid="view-paf-tab-current"
              className={cn(
                "relative mb-1 block w-full cursor-pointer border-0 bg-transparent py-2.5 pl-3.5 pr-2.5 text-left text-sm text-[#3c3c3c]",
                treatmentPanel === "current"
                  ? "bg-white font-semibold shadow-[inset_3px_0_0_#00a03b]"
                  : "hover:bg-black/[0.03]"
              )}
              onClick={() => setTreatmentPanel("current")}
            >
              <span className="inline-flex flex-wrap items-center gap-1.5">
                Current PAF
                <span className="ml-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00a03b] before:mr-0 before:inline-block before:size-2 before:shrink-0 before:rounded-full before:bg-[#00a03b] before:content-['']">
                  Available
                </span>
              </span>
            </button>

            {/* Tab: PAF history — prior prescriptions list (`treatmentPanel === "history"`) */}
            <button
              type="button"
              data-testid="view-paf-tab-history"
              className={cn(
                "relative mb-1 block w-full cursor-pointer border-0 bg-transparent py-2.5 pl-3.5 pr-2.5 text-left text-sm text-[#3c3c3c]",
                treatmentPanel === "history"
                  ? "bg-white font-semibold shadow-[inset_3px_0_0_#00a03b]"
                  : "hover:bg-black/[0.03]"
              )}
              onClick={() => setTreatmentPanel("history")}
            >
              PAF History
            </button>

            {/* Tab: non-compliance — non-compliance history (`treatmentPanel === "noncompliance"`) */}
            <button
              type="button"
              data-testid="view-paf-tab-noncompliance"
              className={cn(
                "relative mb-1 block w-full cursor-pointer border-0 bg-transparent py-2.5 pl-3.5 pr-2.5 text-left text-sm text-[#3c3c3c]",
                treatmentPanel === "noncompliance"
                  ? "bg-white font-semibold shadow-[inset_3px_0_0_#00a03b]"
                  : "hover:bg-black/[0.03]"
              )}
              onClick={() => setTreatmentPanel("noncompliance")}
            >
              Non-compliance History
            </button>

            {showAuditTrailTab && (
              <button
                type="button"
                data-testid="view-paf-tab-audit"
                className={cn(
                  "relative mb-1 block w-full cursor-pointer border-0 bg-transparent py-2.5 pl-3.5 pr-2.5 text-left text-sm text-[#3c3c3c]",
                  treatmentPanel === "audit"
                    ? "bg-white font-semibold shadow-[inset_3px_0_0_#00a03b]"
                    : "hover:bg-black/[0.03]"
                )}
                onClick={() => setTreatmentPanel("audit")}
              >
                Audit Trail
              </button>
            )}

            {!isCreateNewPAF && onCreateNewCycle && (
              <div className="mt-5 px-0.5">
                <button
                  type="button"
                  className="green-button green-haze-background !h-10 !w-full !text-sm !text-white"
                  onClick={onCreateNewCycle}
                  data-testid="view-paf-create-new-cycle"
                >
                  Create New Cycle
                </button>
              </div>
            )}
          </aside>
          <div
            className="min-w-0 flex-1 px-5 py-5 pb-6"
            data-testid="view-paf-panel-content"
          >
            {treatmentPanel === "history" && (
              <div className="px-1" data-testid="view-paf-history-panel">
                {historyPafs?.length ? (
                  <ul className="space-y-3" data-testid="view-paf-history-list">
                    {historyPafs.map((p) => {
                      const totalWeeks = historyTotalSupplyWeeks(p);

                      return (
                        <li
                          key={p.id}
                          className="rounded-md border border-[#e0e0e0] bg-[#fafafa] px-4 py-3 text-sm text-[#3c3c3c]"
                          data-testid={`view-paf-history-item-${p.id}`}
                        >
                          <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-molecule-label-${p.id}`}>
                                Molecule
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.06)}
                              >
                                {historyMoleculeLabel(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-date-of-paf-label-${p.id}`}>
                                Date of PAF
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.08)}
                              >
                                {historyPafDate(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-indication-label-${p.id}`}>
                                Indication
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.1)}
                              >
                                {historyIndicationLabel(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-status-label-${p.id}`}>
                                Status
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.12)}
                              >
                                {p.status_name ?? p.status ?? "—"}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-date-of-dispensing-label-${p.id}`}>
                                Date of Dispensing
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.14)}
                              >
                                {historyDispensingDate(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-paf-id-label-${p.id}`}>
                                PAF ID
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.16)}
                              >
                                {p.id ?? "—"}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-compliance-indicator-label-${p.id}`}>
                                Compliance Indicator
                              </span>{" "}
                              <motion.span
                                className="inline-flex font-semibold"
                                {...textMotionProps(0.18)}
                              >
                                <ComplianceIndicator
                                  value={resolveComplianceIndicator(p)}
                                  showLabel
                                />
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-dosage-strength-label-${p.id}`}>
                                Dosage Strength
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.2)}
                              >
                                {historyDosageStrength(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-number-of-capsules-label-${p.id}`}>
                                Number of capsules
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.22)}
                              >
                                {historyCapsules(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-supply-weeks-per-cycle-label-${p.id}`}>
                                Supply Weeks per cycle
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.24)}
                              >
                                {historyWeeksPerCycle(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-number-of-cycles-label-${p.id}`}>
                                Number of cycles
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.26)}
                              >
                                {historyNCycles(p)}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-total-supply-weeks-label-${p.id}`}>
                                Total supply weeks
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.28)}
                              >
                                {totalWeeks ? (
                                  <span className="inline-block rounded-full bg-[#e8f4fc] px-2.5 py-0.5 text-xs font-semibold text-[#3c3c3c]">
                                    {totalWeeks}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </motion.span>
                            </div>
                            <div>
                              <span className={fieldLabel} data-testid={`view-paf-history-dispensing-brand-label-${p.id}`}>
                                Dispensing Brand
                              </span>{" "}
                              <motion.span
                                className="font-semibold"
                                {...textMotionProps(0.3)}
                              >
                                {p.marketing_holder ?? "—"}
                              </motion.span>
                            </div>
                          </div>
                          <div className="mt-3 flex justify-end">
                            <DownloadButton
                              defaultWidth={80}
                              className="h-[30px]"
                              data-testid={`view-paf-history-download-${p.id}`}
                              disabled={
                                !(
                                  patientDetails?.id ??
                                  form?.patient?.id ??
                                  form?.patient_id
                                )
                              }
                              aria-label={`Download PAF ${p.id ?? ""}`}
                              onDownload={(e) => {
                                e.stopPropagation();
                                return downloadPafPdfForPatient(
                                  patientDetails?.id ??
                                  form?.patient?.id ??
                                  form?.patient_id,
                                  `PAF-${p.id}`
                                );
                              }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div
                    className="px-4 py-8 text-sm leading-relaxed text-[#8c8c8c]"
                    data-testid="view-paf-history-empty"
                  >
                    No previous PAF records for this patient.
                  </div>
                )}
              </div>
            )}
            {treatmentPanel === "noncompliance" && (
              <NonComplianceHistoryPanel
                complaints={complaints}
                indicatorsByPrescriptionId={indicatorsByPrescriptionId}
                loading={complaintsLoading}
                complaintUserKey={complaintUserKey}
              />
            )}
            {treatmentPanel === "audit" && (
              <div className="px-1" data-testid="view-paf-audit-panel">
                {auditTrailLoading ? (
                  <div
                    className="flex justify-center py-12"
                    data-testid="view-paf-audit-loading"
                  >
                    <BlobLoader size={80} showLoadingText={false} />
                  </div>
                ) : auditTrailError ? (
                  <div
                    className="px-4 py-8 text-sm leading-relaxed text-[#e41818]"
                    data-testid="view-paf-audit-error"
                  >
                    {auditTrailError}
                  </div>
                ) : auditTrailEntry ? (
                  <PrescriptionAuditTrailCard entry={auditTrailEntry} />
                ) : (
                  <div
                    className="px-4 py-8 text-sm leading-relaxed text-[#8c8c8c]"
                    data-testid="view-paf-audit-empty"
                  >
                    No audit trail records for this prescription.
                  </div>
                )}
              </div>
            )}
            {treatmentPanel === "current" && (
              <>
                <div
                  className="mb-2 grid grid-cols-2 gap-3 gap-x-3.5 md:grid-cols-4 xl:grid-cols-7"
                  data-testid="view-paf-current-summary"
                >
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-molecule-label">Molecule</div>
                    <motion.div
                      className={fieldValue}
                      {...textMotionProps(0.1)}
                    >
                      {form?.molecule?.label
                        ? form.molecule?.label
                        : form?.drug ?? form?.product ?? "—"}
                    </motion.div>
                  </div>
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-date-of-paf-label">Date of PAF</div>
                    <motion.div
                      className={cn(
                        fieldValue,
                        markInRedPrescribedOnField(form, {
                          isPatientInitiationOrRenew,
                          pathSegment3,
                        }) && "text-[#e41818]"
                      )}
                      {...textMotionProps(0.1)}
                    >
                      {isCreateNewPAF
                        ? moment(new Date()).format("DD-MMM-YYYY")
                        : moment(form?.pfa_issued).format("DD-MMM-YYYY")}
                    </motion.div>
                  </div>
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-indication-label">Indication</div>
                    <motion.div
                      className={cn(
                        "font-size-16 font-semibold text-[#3c3c3c]",
                        extractIndication(form)?.toLowerCase()?.includes("other") &&
                        (isPrescriber || isConnectedNurse) &&
                        "text-[#e41818]"
                      )}
                      {...textMotionProps(0.1)}
                    >
                      {extractIndication(form) || (
                        <span className="red-label font-size-16">Incomplete</span>
                      )}
                    </motion.div>
                  </div>
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-status-label">Status</div>
                    <motion.div
                      className={fieldValue}
                      {...textMotionProps(0.1)}
                    >
                      {getPafStatus(form) || "—"}
                    </motion.div>
                  </div>
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-date-of-dispensing-label">Date of Dispensing</div>
                    <motion.div
                      className={fieldValue}
                      {...textMotionProps(0.1)}
                    >
                      {dispensingDateDisplay()}
                    </motion.div>
                  </div>
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-paf-id-label">PAF ID</div>
                    <motion.div
                      className={fieldValue}
                      {...textMotionProps(0.1)}
                    >
                      {form?.id ?? "—"}
                    </motion.div>
                  </div>
                  <div>
                    <div className={fieldLabel} data-testid="view-paf-compliance-indicator-label">Compliance Indicator</div>
                    <motion.div
                      className={cn(fieldValue, "flex items-center")}
                      {...textMotionProps(0.1)}
                    >
                      <ComplianceIndicator
                        value={currentComplianceIndicator} showLabel
                      />
                    </motion.div>
                  </div>
                </div>

                {resolvePatientCategoryLabel(form, pathSegment3) ===
                  "Woman of Childbearing Potential" && (
                    <div className="mt-3 w-full pb-5">
                      <label
                        className={pafSectionLabelClass}
                        data-testid="view-paf-last-negative-pregnancy-test-label"
                      >
                        Last Negative Pregnancy Test
                      </label>
                      <div
                        className={
                          "font-size-16 display-flex-tooltip " +
                          (markInRedLastNegativePregnancyTest(form, isCreateNewPAF)
                            ? "red-label"
                            : "") +
                          (!isCreateNewPAF &&
                            !is_form_ready_form_approve &&
                            is_preganacy_test_changes
                            ? " green-haze"
                            : "")
                        }
                      >
                        {formatLastNegativePregnancyDate(form) || (
                          <span className="red-label font-size-16">
                            Incomplete
                          </span>
                        )}
                        {!isCreateNewPAF && !!form?.history?.pregnancy_test && (
                          <ToolTipHover
                            quantity={""}
                            name={form?.history?.pharmacy_user}
                            date={moment(form?.history?.ammend_date).format(
                              "DD / MM / YYYY"
                            )}
                            oldValue={moment(
                              form?.history?.pregnancy_test
                            ).format("DD / MM / YYYY")}
                            newValue={moment(form?.pregnancy_test).format(
                              "DD / MM / YYYY"
                            )}
                          />
                        )}

                        {!!hideEditButton && !isPafEditMode && isFormApproved === null && (
                          <React.Fragment>
                            <span
                              className={dosageEditLinkClass}
                              onClick={(event) =>
                                editField(event, "lastNegativePregnanacyTest")
                              }
                              data-testid="view-paf-edit-pregnancy-test"
                              role="button"
                            >
                              EDIT
                            </span>
                          </React.Fragment>
                        )}
                      </div>
                      {isInpatientPharmacyEditablePafStatus(form) &&
                        isLastPregancyTestActive === true && (
                          <CalendarComponent
                            close={setIsLastPregancyTestActive}
                            onClose={() => setIsLastPregancyTestActive(false)}
                            selectRange={false}
                            maxDate={new Date()}
                            setDateRaw={(date) => {
                              handleChangeSubmitedForm("pregnancy_test", date);
                            }}
                            date={
                              form?.patient?.pregnancy_test ||
                                form?.pregnancy_test
                                ? moment(
                                  form?.patient?.pregnancy_test ||
                                  form?.pregnancy_test
                                ).toDate()
                                : moment(new Date()).toDate()
                            }
                            form={form}
                            onResetCalendar={() => {
                              deleteProperty("pregnancy_test");
                            }}
                          />
                        )}
                    </div>
                  )}

                {getPafStatus(form) === "Submitted" && isPrescribedOn === true && (
                  <div className="mt-2 w-full">
                    <label
                      className={pafSectionLabelClass}
                      data-testid="view-paf-adjust-date-of-paf-label"
                    >
                      Adjust Date of PAF (prescriber date)
                    </label>
                    <CalendarComponent
                      close={setIsPrescribedOn}
                      selectRange={false}
                      maxDate={new Date()}
                      setDateRaw={(date) => {
                        handleChangeSubmitedForm("prescribed_on", date);
                      }}
                      date={new Date(form?.pfa_issued)}
                    />
                  </div>
                )}
                <div>
                  {prescriptionField?.prescription_confirmation_risks?.map((item) => (
                    <div key={item.id} className="mt-2 mb-2">
                      <label
                        className={
                          "custom-checkbox font-size-16 dove-gray" +
                          (checkedItems[`risk-checkbox-${item.id}`] === false
                            ? " red-label"
                            : "")
                        }
                        htmlFor={`risk-checkbox-${item.id}`}
                        data-testid={`view-paf-risk-confirmation-${item.id}-label`}
                      >
                        {item.confirmation_text}
                        <input
                          type="checkbox"
                          id="confirmation"
                          name="confirmation"
                          checked={checkedItems[`risk-checkbox-${item.id}`] || false}
                          onChange={
                            isInpatientPharmacyEditablePafStatus(form) && isPharmacy
                              ? (event) => handleChangeSubmitedForm("confirmation", event)
                              : () => true
                          }
                          data-testid={`view-paf-risk-confirmation-${item.id}-checkbox`}
                        />
                        <span className="checkmark mt-0" />
                      </label>
                    </div>
                  ))}
                </div>
                <div>
                  {form?.prescription_confirmation_risks?.map((item, index) => (
                    <div key={index} className="mt-2 mb-2">
                      <label
                        className={
                          "custom-checkbox font-size-16 dove-gray" +
                          (item.confirmation_risk_patient === false ? " red-label" : "") +
                          (item.confirmation_checked_by_pharmacist === true
                            ? " green-haze"
                            : "")
                        }
                        htmlFor={`confirmation-${index}`}
                        data-testid={`view-paf-confirmation-${index}-label`}
                      >
                        {item.confirmation_text}{" "}
                        <input
                          type="checkbox"
                          id={`confirmation-${index}`}
                          name={`confirmation-${index}`}
                          checked={item.confirmation_risk_patient}
                          onChange={(event) => handleChange(event, index)}
                          disabled={
                            isPharmacy && isFormApproved !== null
                              ? true
                              : initialConfirmation[index]?.confirmation_risk_patient
                          }
                          data-testid={`view-paf-confirmation-${index}-checkbox`}
                        />
                        <span className="checkmark mt-0" />
                      </label>
                    </div>
                  ))}
                </div>
                <hr className="my-6 border-0 border-t border-[#dcdcdc]" />
                <div className={dosageSupplyCardClass} data-testid="view-paf-dosage-supply">
                  <div className="grid grid-cols-1 gap-x-6 gap-y-3 border-b border-[#e8e8e8] pb-4 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="min-w-0">
                      <label
                        className={cn(
                          dosageSupplyFieldLabel,
                          "!mb-0 block",
                          aditionalOptionsState?.showErrors &&
                          !aditionalOptionsState?.validation?.capsuleStrength?.isValid &&
                          "!text-[#e41818]"
                        )}
                        data-testid="view-paf-capsule-strength-label"
                      >
                        {prescriptionField?.schedule_display_type === "weeks_per_cycle" ||
                          form?.schedule_display_type === "weeks_per_cycle"
                          ? "Dosage Strength*"
                          : disableAditionalOptions()
                            ? `${capitalizeFirstLetter(form?.unit_label || 'capsule')} Strength`
                            : `${capitalizeFirstLetter(form?.unit_label || 'capsule')} Strength`}
                      </label>
                    </div>
                    {scheduleDisplayType === "weeks_per_cycle" ? (
                      <div className="min-w-0">
                        <label
                          className={cn(
                            dosageSupplyFieldLabel,
                            "!mb-0 block pr-2.5",
                            aditionalOptionsState?.showErrors &&
                            !aditionalOptionsState?.validation?.numberWeeksPerCycle
                              ?.isValid &&
                            "!text-[#e41818]"
                          )}
                          data-testid="view-paf-supply-weeks-per-cycle-label"
                        >
                          Supply weeks per cycle:*
                        </label>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <label
                          className={cn(
                            dosageSupplyFieldLabel,
                            "!mb-0 block pr-2.5",
                            aditionalOptionsState?.showErrors &&
                            !aditionalOptionsState?.validation?.capsulePerCycle
                              ?.isValid &&
                            "!text-[#e41818]"
                          )}
                          data-testid="view-paf-capsules-per-cycle-label"
                        >
                          {prescriptionField?.schedule_display_type === "weeks" ||
                            displayWeeks
                            ? `Number of ${pluralizeUnit(capitalizeFirstLetter(form?.unit_label || 'capsule'))} per 4 week supply:${!disableAditionalOptions() ? "*" : ""
                            }`
                            : disableAditionalOptions()
                              ? `${pluralizeUnit(capitalizeFirstLetter(form?.unit_label || 'capsule'))} per Cycle:`
                              : `${pluralizeUnit(capitalizeFirstLetter(form?.unit_label || 'capsule'))} per Cycle*:`}
                        </label>
                      </div>
                    )}

                    <div className="min-w-0">
                      <label
                        className={cn(
                          dosageSupplyFieldLabel,
                          "!mb-0 block",
                          aditionalOptionsState?.showErrors &&
                          !aditionalOptionsState?.validation?.numberOfCycles?.isValid &&
                          "!text-[#e41818]"
                        )}
                        data-testid="view-paf-number-of-cycles-label"
                      >
                        {getLabelForScheduleType(scheduleDisplayType)}
                      </label>
                    </div>
                    <div className="min-w-0">
                      <label
                        className={cn(dosageSupplyFieldLabel, "!mb-0 block")}
                        data-testid="view-paf-total-supply-label"
                      >
                        {getTotalCapsulesLabel(scheduleDisplayType)}
                      </label>
                    </div>
                    <div className="min-w-0">
                      <label
                        className={cn(dosageSupplyFieldLabel, "!mb-0 block")}
                        data-testid="view-paf-total-capsules-supplied-label"
                      >
                        Total {pluralizeUnit(capitalizeFirstLetter(form?.unit_label || "capsule"))} Supplied
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="min-w-0">
                      {renderCapsuleStrengthColumn()}
                    </div>

                    <div className="min-w-0">
                      {renderCapsulePerCycleColumn()}
                      {scheduleDisplayType === "weeks_per_cycle" &&
                        (isPafEditMode || (!isFormApproved &&
                        form?.new_weeks_per_cycle?.id === 0)) && (
                          <div className="row mb-3 mt-2">
                            <div className="col row-other">
                              <label data-testid="view-paf-other-weeks-per-cycle-label">Other Weeks:*</label>
                              <input
                                type="number"
                                name={`weeks_per_cycle_other`}
                                value={
                                  form?.new_weeks_per_cycle?.id === 0
                                    ? form?.new_weeks_per_cycle_other ?? 1
                                    : form?.new_weeks_per_cycle?.id ?? 1
                                }
                                onChange={(e) => {
                                  handlePrescriptionFields(
                                    e.target.value,
                                    "weeks_per_cycle_other"
                                  );
                                }}
                                placeholder="Other Weeks"
                                min={1}
                                data-testid="view-paf-other-weeks-per-cycle-input"
                              />
                            </div>
                          </div>
                        )}
                      {scheduleDisplayType !== "weeks_per_cycle" &&
                        !!form?.strengths &&
                        form?.strengths?.map((element) =>
                          !!element?.new_capsules &&
                            (isPafEditMode || !isFormApproved) &&
                            scheduleDisplayType !== "weeks_per_cycle" &&
                            element?.new_capsules === "Other (please state)" ? (
                            <div className="row row-other mb-3 mt-2" key={element.id}>
                              <div className="col">
                                <label data-testid={`view-paf-other-capsules-${element.id}-label`}>
                                  Other{" "}
                                  {pluralizeUnit(
                                    capitalizeFirstLetter(form?.unit_label || "capsule")
                                  )}{" "}
                                  per {displayWeeks ? "4 Weeks:*" : "Cycle:*"}
                                </label>
                                <input
                                  type="number"
                                  onChange={(e) => {
                                    handlePrescriptionFields(
                                      e.target.value,
                                      "capsules",
                                      form?.strengths?.indexOf(element)
                                    );
                                  }}
                                  value={
                                    other_capsules_value[form?.strengths?.indexOf(element)]
                                      ?.new_capsules_other === null
                                      ? 1
                                      : other_capsules_value[
                                        form?.strengths?.indexOf(element)
                                      ]?.new_capsules_other
                                  }
                                  name={`capsule_cycle_other_${element.id}`}
                                  placeholder={`Other ${pluralizeUnit(
                                    capitalizeFirstLetter(form?.unit_label || "capsule")
                                  )} per Cycle`}
                                  min={1}
                                  data-testid={`view-paf-other-capsules-${element.id}-input`}
                                />
                              </div>
                            </div>
                          ) : null
                        )}
                    </div>

                    <div className="min-w-0">
                      {renderNumberOfCyclesColumn()}
                      {form?.new_capsule_cycles &&
                        (isPafEditMode || !isFormApproved) &&
                        form?.new_capsule_cycles.label === "Other (Please State)" && (
                          <div className="row mb-3 mt-2">
                            <div className="col row-other">
                              <label data-testid="view-paf-other-weeks-label">Other Weeks:*</label>
                              <input
                                type="number"
                                name={`capsule_cycle_other_$`}
                                value={
                                  !!form?.n_weeks_other ? form?.n_weeks_other : 1 || ""
                                }
                                onChange={(e) => {
                                  handlePrescriptionFields(
                                    e.target.value,
                                    "number_weeks_other"
                                  );
                                }}
                                placeholder="Other Weeks"
                                min={1}
                                data-testid="view-paf-other-weeks-input"
                              />
                            </div>
                          </div>
                        )}
                    </div>

                    {renderTotalSupplyColumn()}
                  </div>
                </div>
                <hr className="my-6 border-0 border-t border-[#dcdcdc]" />

                <div className="mt-2" data-testid="view-paf-parties">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-5 py-3.5 md:grid-cols-2">
                    <div>
                      <div className={fieldLabel} data-testid="view-paf-prescriber-label">Prescriber</div>
                      <motion.div className={fieldValue} {...textMotionProps(0.1)}>
                        {displayConditionallyPrescriberName()}
                      </motion.div>
                    </div>
                    <div>
                      <div className={fieldLabel} data-testid="view-paf-parties-treating-hospital-label">Treating Hospital</div>
                      <motion.div className={fieldValue} {...textMotionProps(0.1)}>
                        {form?.hospital_name_and_address ||
                          form?.patient?.hospital ||
                          handedHospitalName}
                      </motion.div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-5 py-3.5 md:grid-cols-2">
                    <div>
                      <div className={fieldLabel} data-testid="view-paf-approving-pharmacist-label">Approving Pharmacist</div>
                      <motion.div className={fieldValue}
                        {...textMotionProps(0.1)}
                      >
                        {displayConditionallyApprovedBy()}
                      </motion.div>
                    </div>
                    <div>
                      <div className={fieldLabel} data-testid="view-paf-pharmacy-address-label">Pharmacy Address</div>
                      <motion.div className={fieldValue}
                        {...textMotionProps(0.1)}
                      >
                        {dispensingForm?.location?.address ||
                          form?.pharmacy_address ||
                          form?.patient?.pharmacy ||
                          getPharmacyFromLocalStorage()}
                      </motion.div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-5 py-3.5 border-b border-[#dcdcdc] md:grid-cols-2">
                    <div>
                      <div className={fieldLabel} data-testid="view-paf-dispenser-label">Dispenser</div>
                      <motion.div className={fieldValue}
                        {...textMotionProps(0.1)}
                      >
                        {form?.dispenser_user || "—"}
                      </motion.div>
                    </div>
                    <div>
                      <div className={fieldLabel} data-testid="view-paf-dispensing-address-label">Dispensing Address</div>
                      <motion.div className={fieldValue}
                        {...textMotionProps(0.1)}
                      >
                        {dispensingForm?.location?.address ||
                          form?.dispensing_address ||
                          form?.patient?.pharmacy ||
                          "—"}
                      </motion.div>
                    </div>
                  </div>
                </div>

                {form.status !== "Rejected" && !isPatientInitiationOrRenew && (
                  <div className="mt-3 w-full">
                    <label
                      className={pafSectionLabelClass}
                      data-testid="view-paf-brand-selected-label"
                    >
                      Brand selected
                    </label>
                    <div className="font-size-16">
                      {form?.marketing_holder || "Awaiting to be Dispensed"}
                    </div>
                  </div>
                )}

                {getPafStatus(form) === "Dispensed" && !isPafEditMode && (
                  <div className="mt-2 w-full">
                    {form?.amended_dispensed_date ? (
                      <label
                        className={pafSectionLabelClass}
                        data-testid="view-paf-nominated-date-of-dispensing-label"
                      >
                        Nominated Date of Dispensing
                      </label>
                    ) : (
                      <label
                        className={pafSectionLabelClass}
                        data-testid="view-paf-dispensed-date-label"
                      >
                        Date of Dispensing
                      </label>
                    )}
                    <div className="font-size-16 flex items-center">
                      {form?.amended_dispensed_date
                        ? moment(form?.amended_dispensed_date).format("DD / MM / YYYY")
                        : moment(form?.dispensed_date).format("DD / MM / YYYY")}
                      {!isCreateNewPAF && !!form?.amended_dispensed_date && (
                        <ToolTipHover
                          quantity={""}
                          name={form?.dispenser_user}
                          date={moment(form?.dispensed_date).format("DD / MM / YYYY")}
                          oldValue={moment(form?.dispensed_date).format("DD / MM / YYYY")}
                          newValue={moment(form?.amended_dispensed_date).format("DD / MM / YYYY")}
                        />
                      )}
                    </div>
                  </div>
                )}

                {isPafEditMode && hasDispensingBrandAssigned(form) && (
                  <PafEditBrandDateFields
                    form={form}
                    setForm={setForm}
                    productId={form?.product_id}
                  />
                )}

                <PrescriptionReviewSummary prescription={form} />
              </>
            )}
            {footer != null && treatmentPanel === "current" && (
              <div
                className="paf-modal-footer pt-6"
                data-testid="view-paf-footer"
              >
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    ""
  );
};

ViewPAF.propTypes = {
  receivedForm: PropTypes.object,
  handleChangeSubmitedForm: PropTypes.func,
  numberOfCylesValues: PropTypes.array,
  isFormApproved: PropTypes.bool,
  isCreateNewPAF: PropTypes.bool,
  handleAditionalOptionsChange: PropTypes.func,
  aditionalOptions: PropTypes.object,
  typeOfPacks: PropTypes.array,
  aditionalOptionsState: PropTypes.object,
  setFormItems: PropTypes.func,
  dispensingForm: PropTypes.object,
  patientDetails: PropTypes.object,
  historyPafs: PropTypes.array,
  footer: PropTypes.node,
  onCreateNewCycle: PropTypes.func,
  isPafEditMode: PropTypes.bool,
  onEditFormChange: PropTypes.func,
};

export default ViewPAF;
